import React, { useState, useEffect, useRef } from 'react';
import articleService from '../../../services/articleService';
import clientService from '../../../services/clientService';
import typeArticleService from '../../../services/typeArticleService';
import localService from '../../../services/localService';
import { useTheme } from '../../../contexts/ThemeContext';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';
import TypeArticleSelectDropdown from '../../common/TypeArticleSelectDropdown';
import { openBarcodeScanner } from '../../../utils/barcodeScanner';

const EditArticleModal = ({ isOpen, article, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    designation: '',
    typeArticleId: '',
    capacite: '',
    marque: '',
    codeABarre: '',
    numeroSerie: '',
    emplacement: '',
    observation: '',
    localId: '',
    clientId: '',
    pressionPermanente: true,
    datetimeFabrication: '',
    enService: true
  });
  const [detailedArticle, setDetailedArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  // Camera scanning states
  const [showCamera, setShowCamera] = useState(false);
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const scannerRef = useRef(null);

  // Dropdown data and loading states
  const [typeArticles, setTypeArticles] = useState([]);
  const [clients, setClients] = useState([]);
  const [locaux, setLocaux] = useState([]);
  const [loadingData, setLoadingData] = useState({
    typeArticles: false,
    clients: false,
    locaux: false
  });

  // Search states for dropdowns
  const [searchStates, setSearchStates] = useState({
    typeArticle: '',
    client: '',
    local: ''
  });

  // Load detailed article data when modal opens
  useEffect(() => {
    if (isOpen && article?.id) {
      loadArticleDetails(article.id);
      fetchTypeArticles();
      fetchClients();
    }
  }, [isOpen, article?.id]);

  // Locaux always follow the selected client
  useEffect(() => {
    if (!isOpen) return;
    fetchLocaux(formData.clientId);
  }, [isOpen, formData.clientId]);

  // Update form data when detailed article is loaded
  useEffect(() => {
    if (detailedArticle) {
      setFormData({
        designation: detailedArticle.designation || '',
        typeArticleId: detailedArticle.typeArticleId?.toString() || '',
        capacite: detailedArticle.capacite?.toString() || '',
        marque: detailedArticle.marque || '',
        codeABarre: detailedArticle.codeABarre || '',
        numeroSerie: detailedArticle.numeroSerie || '',
        emplacement: detailedArticle.emplacement || '',
        observation: detailedArticle.observation || '',
        localId: detailedArticle.localId?.toString() || '',
        clientId: detailedArticle.clientId?.toString() || '',
        pressionPermanente: detailedArticle.pressionPermanente || false,
        datetimeFabrication: detailedArticle.datetimeFabrication ? 
          detailedArticle.datetimeFabrication.split('T')[0] : '',
        enService: detailedArticle.enService !== undefined ? detailedArticle.enService : true // Add this field
      });
    }
  }, [detailedArticle]);

  const loadArticleDetails = async (articleId) => {
    setLoadingDetails(true);
    setError('');

    try {
      const result = await articleService.getArticleById(articleId);
      
      if (result.success) {
        setDetailedArticle(result.data);
      } else {
        setError(result.message || 'Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error('Error loading article details:', error);
      setError('Erreur lors du chargement des détails de l\'article');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchTypeArticles = async () => {
    setLoadingData(prev => ({ ...prev, typeArticles: true }));
    try {
      const result = await typeArticleService.getTypeArticles(50, 1);
      
      if (result.success && result.data?.list) {
        setTypeArticles(result.data.list);
      } else if (result.success && result.data?.data) {
        setTypeArticles(result.data.data);
      } else {
        setTypeArticles([]);
      }
    } catch (error) {
      console.error('Error fetching type articles:', error);
      setTypeArticles([]);
    } finally {
      setLoadingData(prev => ({ ...prev, typeArticles: false }));
    }
  };

  const fetchClients = async () => {
    setLoadingData(prev => ({ ...prev, clients: true }));
    try {
      const result = await clientService.getClients(100, 1);
      
      if (result.success && result.data?.list) {
        setClients(result.data.list);
      } else if (result.success && result.data?.data) {
        setClients(result.data.data);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoadingData(prev => ({ ...prev, clients: false }));
    }
  };

  const fetchLocaux = async (clientId) => {
    setLoadingData(prev => ({ ...prev, locaux: true }));
    try {
      // With a client selected, only that client's locaux; otherwise fall back to all
      const result = clientId
        ? await localService.getLocauxByClient(clientId)
        : await localService.getLocaux(100, 1);

      if (result.success && Array.isArray(result.data)) {
        setLocaux(result.data);
      } else if (result.success && result.data?.list) {
        setLocaux(result.data.list);
      } else if (result.success && result.data?.data) {
        setLocaux(result.data.data);
      } else {
        setLocaux([]);
      }
    } catch (error) {
      console.error('Error fetching locaux:', error);
      setLocaux([]);
    } finally {
      setLoadingData(prev => ({ ...prev, locaux: false }));
    }
  };

  // Stop scanning function
  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
      scannerRef.current = null;
    }
    setBarcodeScanning(false);
  };

  // Start barcode scanning using Quagga2
  const startBarcodeScanning = () => {
    setBarcodeScanning(true);
    setShowCamera(true);
    const scanner = openBarcodeScanner({
      modalId: 'barcode-scanner-modal-edit',
      scannerId: 'quagga-reader-edit',
      onDetected: (barcode) => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        handleScanResult(barcode);
      },
      onError: (err) => {
        console.error('Scanner error:', err);
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Caméra non disponible. Saisissez le code-barres manuellement (8 chiffres maximum):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            handleScanResult(trimmedInput);
          }
        }
      },
      onManualEntry: () => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Saisissez le code-barres manuellement (max 8 chiffres):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            handleScanResult(trimmedInput);
          }
        }
      },
    });
    scannerRef.current = scanner;
  };

  // Handle QR/Barcode scan result with validation
  const handleScanResult = (result) => {
    if (result) {
      // Validate barcode length before processing
      if (result.length > 8) {
        setError('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
        return;
      }
      
      setFormData(prev => ({ ...prev, codeABarre: result }));
      setShowCamera(false);
      console.log(`Code scanné: ${result}`);
    }
  };

  // Clean up camera on modal close
  useEffect(() => {
    if (!isOpen) {
      stopScanning();
    }
  }, [isOpen]);

  // Update form data when detailed article is loaded
  useEffect(() => {
    if (detailedArticle) {
      setFormData({
        designation: detailedArticle.designation || '',
        typeArticleId: detailedArticle.typeArticleId?.toString() || '',
        capacite: detailedArticle.capacite?.toString() || '',
        marque: detailedArticle.marque || '',
        codeABarre: detailedArticle.codeABarre || '',
        numeroSerie: detailedArticle.numeroSerie || '',
        emplacement: detailedArticle.emplacement || '',
        observation: detailedArticle.observation || '',
        localId: detailedArticle.localId?.toString() || '',
        clientId: detailedArticle.clientId?.toString() || '',
        pressionPermanente: detailedArticle.pressionPermanente || false,
        datetimeFabrication: detailedArticle.datetimeFabrication ? 
          detailedArticle.datetimeFabrication.split('T')[0] : '',
        enService: detailedArticle.enService !== undefined ? detailedArticle.enService : true // Add this field
      });
    }
  }, [detailedArticle]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearchChange = (field, value) => {
    setSearchStates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter functions for dropdowns
  const getFilteredTypeArticles = () => {
    if (!searchStates.typeArticle) return typeArticles;
    return typeArticles.filter(type => 
      type.designation.toLowerCase().includes(searchStates.typeArticle.toLowerCase())
    );
  };

  const getFilteredClients = () => {
    if (!searchStates.client) return clients;
    return clients.filter(client => 
      (client.raisonSocial || '').toLowerCase().includes(searchStates.client.toLowerCase()) ||
      `${client.nom || ''} ${client.prenom || ''}`.toLowerCase().includes(searchStates.client.toLowerCase()) ||
      (client.matriculeFiscal || '').toLowerCase().includes(searchStates.client.toLowerCase())
    );
  };

  const getFilteredLocaux = () => {
    if (!searchStates.local) return locaux;
    return locaux.filter(local => 
      (local.adresse || '').toLowerCase().includes(searchStates.local.toLowerCase()) ||
      (local.responsable || '').toLowerCase().includes(searchStates.local.toLowerCase())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare the payload according to the API specification
      const payload = {
        id: article.id,
        designation: formData.designation.trim(),
        localId: formData.localId ? parseInt(formData.localId, 10) : null,
        local: null, // Only send ID via localId
        client: null, // Only send ID via clientId
        clientId: formData.clientId ? parseInt(formData.clientId, 10) : null,
        emplacement: formData.emplacement.trim(),
        codeABarre: formData.codeABarre.trim(),
        observation: formData.observation.trim() || null,
        typeArticle: null, // Only send ID via typeArticleId
        typeArticleId: parseInt(formData.typeArticleId, 10),
        capacite: parseFloat(formData.capacite),
        pressionPermanente: formData.pressionPermanente,
        marque: formData.marque.trim(),
        datetimeFabrication: formData.datetimeFabrication ? new Date(formData.datetimeFabrication).toISOString() : null,
        numeroSerie: formData.numeroSerie.trim() || null,
        enService: formData.enService // Add this field
      };

      console.log('Sending update payload:', payload);

      // Use the articleService which handles authentication
      const result = await articleService.updateArticle(article.id, payload);
      
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.message || 'Erreur lors de la modification de l\'article');
      }
    } catch (error) {
      console.error('Update article error:', error);
      setError('Erreur lors de la modification de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen || !article) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${
          isDarkMode
            ? 'bg-gray-800' // Match modal body in dark mode
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                <i className="fas fa-edit text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Modifier l'Article</h2>
                <p className={`text-sm ${isDarkMode ? 'text-primary-200' : 'text-primary-100'}`}>ID: {article.id}</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {/* Loading detailed data */}
            {loadingDetails && (
              <div className="flex items-center justify-center py-12">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mr-4 ${
                  isDarkMode ? 'border-dark-primary-600' : 'border-primary-600'
                }`}></div>
                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  Chargement des données...
                </span>
              </div>
            )}

            {error && (
              <div className={`mb-6 p-4 rounded-xl border ${
                isDarkMode 
                  ? 'bg-red-900/50 border-red-800 text-red-300' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <div className="flex items-center">
                  <i className={`fas fa-exclamation-circle mr-3 ${
                    isDarkMode ? 'text-red-400' : 'text-red-500'
                  }`}></i>
                  <div>
                    <div className="font-medium">Erreur</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Show form only when detailed data is loaded */}
            {!loadingDetails && detailedArticle && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Client Information Display */}
                {detailedArticle.client && (
                  <div className={`rounded-xl p-6 ${
                    isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-blue-50'
                  }`}>
                    <div className="flex items-center mb-4">
                      <div className={`p-2 rounded-lg mr-3 ${
                        isDarkMode ? 'bg-gray-800' : 'bg-blue-100'
                      }`}>
                        <i className={`fas fa-info-circle ${
                          isDarkMode ? 'text-gray-300' : 'text-blue-600'
                        }`}></i>
                      </div>
                      <h3 className={`text-lg font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>Informations Client Actuelles</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-blue-700'
                        }`}>Client: </span>
                        <span className={isDarkMode ? 'text-gray-100' : 'text-blue-900'}>
                          {detailedArticle.client.raisonSocial || 
                           `${detailedArticle.client.nom || ''} ${detailedArticle.client.prenom || ''}`.trim() || 
                           'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-blue-700'
                        }`}>Matricule: </span>
                        <span className={isDarkMode ? 'text-gray-100' : 'text-blue-900'}>
                          {detailedArticle.client.matriculeFiscal || 'N/A'}
                        </span>
                      </div>
                      {detailedArticle.local && (
                        <>
                          <div>
                            <span className={`font-medium ${
                              isDarkMode ? 'text-gray-200' : 'text-blue-700'
                            }`}>Local: </span>
                            <span className={isDarkMode ? 'text-gray-100' : 'text-blue-900'}>
                              {detailedArticle.local.adresse || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className={`font-medium ${
                              isDarkMode ? 'text-gray-200' : 'text-blue-700'
                            }`}>Responsable: </span>
                            <span className={isDarkMode ? 'text-gray-100' : 'text-blue-900'}>
                              {detailedArticle.local.responsable || 'N/A'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${
                      isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'
                    }`}>
                      <i className={`fas fa-info-circle ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Informations générales</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-tag mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Désignation *
                      </label>
                      <input 
                        type="text" 
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                        }`}
                        placeholder="Ex: EXT001, EXT-POUDRE-001"
                      />
                    </div>
                    
                    {/* Type Article with Integrated Search */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-layer-group mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Type d'article *
                      </label>
                      <TypeArticleSelectDropdown
                        value={formData.typeArticleId}
                        onChange={val => setFormData(prev => ({ ...prev, typeArticleId: val }))}
                        options={typeArticles}
                        loading={loadingData.typeArticles}
                        disabled={loadingData.typeArticles}
                        isDarkMode={isDarkMode}
                        required={true}
                        placeholder="Sélectionner un type"
                      />
                    </div>
                  </div>
                </div>

                {/* Technical Specifications */}
                <div className={`rounded-xl p-6 ${
                  isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-green-50'
                }`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-green-100'
                    }`}>
                      <i className={`fas fa-cogs ${
                        isDarkMode ? 'text-gray-300' : 'text-green-600'
                      }`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Spécifications techniques</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-weight mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Capacité (kg) *
                      </label>
                      <input 
                        type="number" 
                        name="capacite"
                        value={formData.capacite}
                        onChange={handleChange}
                        required
                        min="0"
                        step="0.1"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                        }`}
                        placeholder="Ex: 6, 2.5, 1"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-industry mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Marque *
                      </label>
                      <input 
                        type="text" 
                        name="marque"
                        value={formData.marque}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                        }`}
                        placeholder="Ex: Tuffco, Gloria, Sicli"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-barcode mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Code à barres *
                      </label>
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          name="codeABarre"
                          value={formData.codeABarre}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Validate length on input
                            if (value.length <= 8) {
                              handleChange(e);
                            } else {
                              setError('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
                            }
                          }}
                          required
                          maxLength={8}
                          className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Ex: 10000588 (max 8 chiffres)"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (barcodeScanning) {
                              stopScanning();
                            } else {
                              startBarcodeScanning();
                            }
                          }}
                          className={`px-2 sm:px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                            barcodeScanning 
                              ? 'bg-red-600 text-white hover:bg-red-700' 
                              : isDarkMode 
                                ? 'bg-primary-700 text-white hover:bg-primary-800' 
                                : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                          title={barcodeScanning ? "Arrêter le scanner" : "Scanner avec la caméra"}
                          disabled={barcodeScanning}
                        >
                          <i className={`fas ${barcodeScanning ? 'fa-stop' : 'fa-camera'} ${barcodeScanning ? 'animate-pulse' : ''} text-sm sm:text-base`}></i>
                          {/* Show text on larger screens only */}
                          <span className="hidden sm:inline ml-1 text-sm">
                            {barcodeScanning ? 'Arrêter' : 'Scanner'}
                          </span>
                        </button>
                      </div>
                      
                      {/* Responsive Camera Scanner Status */}
                      {barcodeScanning && (
                        <div className={`mt-2 p-2 sm:p-3 rounded-lg border-2 border-dashed ${
                          isDarkMode 
                            ? 'border-primary-700 bg-gray-900 text-primary-300' 
                            : 'border-primary-500 bg-primary-50 text-primary-700'
                        } text-center text-xs sm:text-sm`}>
                          <div className="flex items-center justify-center gap-1 sm:gap-2">
                            <i className="fas fa-camera animate-pulse text-sm"></i>
                            <span className="text-xs sm:text-sm">
                              <span className="hidden sm:inline">Scanner activé - Pointez vers le code-barres</span>
                              <span className="sm:hidden">Scanner actif</span>
                            </span>
                          </div>
                          {/* Additional mobile tip */}
                          <div className="mt-1 text-xs opacity-75 sm:hidden">
                            Pointez vers le code-barres (max 8 chiffres)
                          </div>
                        </div>
                      )}
                      
                      {/* Length validation indicator */}
                      {formData.codeABarre && formData.codeABarre.length > 8 && (
                        <div className="mt-1 text-xs text-red-600 flex items-center">
                          <i className="fas fa-exclamation-triangle mr-1"></i>
                          <span>Maximum 8 chiffres autorisés</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-hashtag mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Numéro de série
                      </label>
                      <input 
                        type="text" 
                        name="numeroSerie"
                        value={formData.numeroSerie}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                        }`}
                        placeholder="Ex: 0285, SN-2024-001"
                      />
                    </div>
                  </div>
                </div>

                {/* Location & Client Info with Integrated Search */}
                <div className={`rounded-xl p-6 ${
                  isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-amber-50'
                }`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-amber-100'
                    }`}>
                      <i className={`fas fa-map-marker-alt ${
                        isDarkMode ? 'text-gray-300' : 'text-amber-600'
                      }`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Localisation et client</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Local with Integrated Search */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-building mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Local
                      </label>
                      <div className="relative">
                        <LocalSelectDropdown
                          value={formData.localId}
                          onChange={val => setFormData(prev => ({ ...prev, localId: val }))}
                          options={locaux}
                          loading={loadingData.locaux}
                          disabled={loadingData.locaux}
                          isDarkMode={isDarkMode}
                          required={false}
                          placeholder="Sélectionner un local"
                        />
                        <div className="absolute top-3 right-10">
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchStates.local}
                            onChange={(e) => handleSearchChange('local', e.target.value)}
                            className={`text-xs bg-transparent border-none outline-none w-20 ${
                              isDarkMode ? 'placeholder-gray-500' : 'placeholder-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                      {searchStates.local && (
                        <div className={`text-xs mt-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {getFilteredLocaux().length} résultat(s) trouvé(s)
                        </div>
                      )}
                    </div>
                    
                    {/* Client with Integrated Search */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-user mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Client
                      </label>
                      <div className="relative">
                        <ClientSelectDropdown
                          value={formData.clientId}
                          onChange={val => setFormData(prev => ({
                            ...prev,
                            clientId: val,
                            // the previous local belongs to the previous client
                            localId: String(val) === String(prev.clientId) ? prev.localId : ''
                          }))}
                          options={clients}
                          loading={loadingData.clients}
                          disabled={loadingData.clients}
                          isDarkMode={isDarkMode}
                          required={false}
                          placeholder="Sélectionner un client"
                        />
                        <div className="absolute top-3 right-10">
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchStates.client}
                            onChange={(e) => handleSearchChange('client', e.target.value)}
                            className={`text-xs bg-transparent border-none outline-none w-20 ${
                              isDarkMode ? 'placeholder-gray-500' : 'placeholder-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                      {searchStates.client && (
                        <div className={`text-xs mt-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {getFilteredClients().length} résultat(s) trouvé(s)
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <i className={`fas fa-location-arrow mr-2 ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}></i>
                      Emplacement *
                    </label>
                    <input 
                      type="text" 
                      name="emplacement"
                      value={formData.emplacement}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                      }`}
                      placeholder="Ex: chambre gardien, hall d'entrée, bureau 201"
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className={`rounded-xl p-6 ${
                  isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-purple-50'
                }`}>
                  <div className="flex items-center mb-4">
                    <div className={`p-2 rounded-lg mr-3 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-purple-100'
                    }`}>
                      <i className={`fas fa-plus-circle ${
                        isDarkMode ? 'text-gray-300' : 'text-purple-600'
                      }`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>Informations complémentaires</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <i className={`fas fa-calendar mr-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}></i>
                        Date de fabrication
                      </label>
                      <input 
                        type="date" 
                        name="datetimeFabrication"
                        value={formData.datetimeFabrication}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                        }`}
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <div className={`flex items-center p-4 rounded-lg border ${
                          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                        }`}>
                          <input 
                            type="checkbox" 
                            name="pressionPermanente"
                            checked={formData.pressionPermanente}
                            onChange={handleChange}
                            className={`mr-3 rounded h-4 w-4 focus:ring-2 focus:ring-offset-2 ${
                              isDarkMode 
                                ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' 
                                : 'text-primary-600 focus:ring-primary-500 border-gray-300'
                            }`}
                          />
                          <label className={`text-sm font-medium flex items-center ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <i className={`fas fa-tachometer-alt mr-2 ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            }`}></i>
                            Pression permanente
                          </label>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <div className={`flex items-center p-4 rounded-lg border ${
                          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                        }`}>
                          <input 
                            type="checkbox" 
                            name="enService"
                            checked={formData.enService}
                            onChange={handleChange}
                            className={`mr-3 rounded h-4 w-4 focus:ring-2 focus:ring-offset-2 ${
                              isDarkMode 
                                ? 'text-dark-primary-600 focus:ring-dark-primary-500 bg-gray-700 border-gray-600' 
                                : 'text-primary-600 focus:ring-primary-500 border-gray-300'
                            }`}
                          />
                          <label className={`text-sm font-medium flex items-center ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <i className={`fas fa-power-off mr-2 ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            }`}></i>
                            En service
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      <i className={`fas fa-sticky-note mr-2 ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}></i>
                      Observations
                    </label>
                    <textarea 
                      rows="4"
                      name="observation"
                      value={formData.observation}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                      }`}
                      placeholder="Observations, notes, remarques particulières sur cet article..."
                    ></textarea>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${
          isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 border rounded-lg transition-colors font-medium ${
                isDarkMode 
                  ? 'text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700' 
                  : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button 
              type="submit"
              onClick={handleSubmit}
              disabled={loading || loadingDetails}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading || loadingDetails
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-dark-primary-600 hover:bg-dark-primary-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Modification...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Sauvegarder les modifications
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditArticleModal;